import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const CACHED_PROFILE_KEY = 'mtsr_cached_profile';
const CACHED_SCHOOL_KEY = 'mtsr_cached_school';

export const DEMO_PROFILES = {
  'bendaharamtsr@gmail.com': {
    id: '3d86b8d8-f651-41ef-bd27-d32b54d3f3ac',
    role: 'bendahara',
    nama: 'Ustaz Bendahara MTSR',
    email: 'bendaharamtsr@gmail.com',
    telefon: '012-3456789',
    school_id: '601a745f-b268-4978-b02c-c40054a9cab6',
    aktif: true,
  },
  'guru.bukhari@mtsr.edu.my': {
    id: 'usr-guru-1',
    role: 'guru',
    nama: 'Cikgu Fatimah',
    email: 'guru.bukhari@mtsr.edu.my',
    telefon: '013-1112222',
    school_id: '601a745f-b268-4978-b02c-c40054a9cab6',
    aktif: true,
  },
  'keluarga.rahman@gmail.com': {
    id: 'usr-parent-1',
    role: 'orangtua',
    nama: 'Encik Rahman',
    email: 'keluarga.rahman@gmail.com',
    telefon: '012-9988776',
    school_id: '601a745f-b268-4978-b02c-c40054a9cab6',
    aktif: true,
  },
};

function getStored(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStored(key, val) {
  try {
    if (val == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* abaikan quota ralat */
  }
}

export function AuthProvider({ children }) {
  const cachedProf = getStored(CACHED_PROFILE_KEY);
  const cachedSch = getStored(CACHED_SCHOOL_KEY);

  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(cachedProf);
  const [school, setSchool] = useState(cachedSch);
  const [loading, setLoading] = useState(!cachedProf);
  const [recovery, setRecovery] = useState(false);

  const inFlightUid = useRef(null);

  const loadProfile = useCallback(async (uid) => {
    if (!uid) {
      setProfile(null);
      setSchool(null);
      setStored(CACHED_PROFILE_KEY, null);
      setStored(CACHED_SCHOOL_KEY, null);
      return null;
    }
    if (inFlightUid.current === uid) return profile;
    inFlightUid.current = uid;

    try {
      const { data: prof } = await supabase.from('profiles').select('*').eq('id', uid).maybeSingle();
      if (prof) {
        setProfile(prof);
        setStored(CACHED_PROFILE_KEY, prof);

        // Muat maklumat sekolah secara tidak menyekat (non-blocking) di latar belakang
        if (prof.school_id) {
          supabase.from('schools').select('*').eq('id', prof.school_id).maybeSingle().then(({ data: sch }) => {
            if (sch) {
              setSchool(sch);
              setStored(CACHED_SCHOOL_KEY, sch);
            }
          }).catch(() => {});
        }
        return prof;
      }
      return null;
    } catch (err) {
      console.warn('[MTSRpay] Profil load warning:', err?.message || err);
      return null;
    } finally {
      inFlightUid.current = null;
    }
  }, [profile]);

  // Log masuk pantas (optimistik untuk akaun demo + respons pantas untuk semua pengguna)
  const signIn = useCallback(async (email, password) => {
    const cleanEmail = email.toLowerCase().trim();
    const demoProf = DEMO_PROFILES[cleanEmail];

    // Optimistik untuk akaun demo: pasang profil segera ke cache
    if (demoProf) {
      setProfile(demoProf);
      setStored(CACHED_PROFILE_KEY, demoProf);
    }

    // Jalankan pengesahan sebenar di Supabase
    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      // Jika kegagalan berlaku pada akaun selain demo, batalkan profil optimistik
      if (!demoProf) {
        setProfile(null);
        setStored(CACHED_PROFILE_KEY, null);
      }
      throw error;
    }

    setSession(data.session);

    // Dapatkan data profil terkini
    const uid = data.user?.id;
    if (uid) {
      const resolvedProf = await loadProfile(uid);
      return resolvedProf || demoProf;
    }
    return demoProf || null;
  }, [loadProfile]);

  useEffect(() => {
    let active = true;
    const timeout = setTimeout(() => {
      if (active) setLoading(false);
    }, 1500);

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data?.session || null);
      if (data?.session?.user?.id) {
        await loadProfile(data.session.user.id);
      }
      setLoading(false);
    }).catch(() => {
      if (active) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!active) return;
      setSession(s);
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
      if (s?.user?.id) {
        await loadProfile(s.user.id);
      } else {
        setProfile(null);
        setSchool(null);
        setStored(CACHED_PROFILE_KEY, null);
        setStored(CACHED_SCHOOL_KEY, null);
      }
      setLoading(false);
    });

    return () => {
      active = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const refreshProfile = useCallback(() => loadProfile(session?.user?.id), [session, loadProfile]);
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      setProfile(null);
      setSchool(null);
      setSession(null);
      setStored(CACHED_PROFILE_KEY, null);
      setStored(CACHED_SCHOOL_KEY, null);
    }
  }, []);

  const value = {
    session,
    user: session?.user || null,
    profile,
    school,
    role: profile?.role || null,
    loading,
    recovery,
    setRecovery,
    refreshProfile,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
